import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Typography,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { activityTypeService } from '../../services/activityTypeService';
import { operationTypeService } from '../../services/operationTypeService';
import { useConfirm } from '../common/components';
import { ActivityTypeList, CreateActivityType, EditActivityType } from './components';
import type { ActivityType, CreateActivityTypeRequest, UpdateActivityTypeRequest } from '../../types/activityType';
import { extractErrorMessage } from '../../utils/errorUtils';
import { setOperationTypesCache } from '../../utils/operationTypeUtils';

export default function ActivityTypePage() {
  const { confirm, confirmDialog } = useConfirm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedActivityType, setSelectedActivityType] = useState<ActivityType | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  const companyId = id ? parseInt(id) : null;

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!companyId) {
        setError('Ідентифікатор компанії відсутній');
        setLoading(false);
        return;
      }

      try {
        // Fetch operation types config first to initialize cache
        const operationTypesConfig = await operationTypeService.getOperationTypesConfig();
        if (cancelled) {
          return;
        }
        setOperationTypesCache(operationTypesConfig.operationTypes);

        // Fetch all activity types (including archived) and filter on the client,
        // same rationale as the company list — this is a small, unpaginated list.
        const activityTypesData = await activityTypeService.getAllByCompany(companyId, true);
        if (cancelled) {
          return;
        }
        setActivityTypes(activityTypesData);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Error fetching data:', err);
        setError('Не вдалося завантажити типи активностей');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const visibleActivityTypes = showOnlyActive
    ? activityTypes.filter((activityType) => !activityType.deactivatedDate)
    : activityTypes;

  const handleCreateActivityType = async (activityTypeData: CreateActivityTypeRequest) => {
    if (!companyId) {
      return;
    }

    try {
      const newActivityType = await activityTypeService.create(activityTypeData);
      setActivityTypes((prev) => [...prev, newActivityType]);
    } catch (err) {
      console.error('Error creating activity type:', err);
      throw err;
    }
  };

  const handleDeleteActivityType = async (activityTypeId: number) => {
    if (!(await confirm('Ви впевнені, що хочете архівувати цей тип активності?'))) {
      return;
    }

    setActionError(null);
    try {
      await activityTypeService.delete(activityTypeId);
      setActivityTypes((prev) => prev.map((at) => (
        at.id === activityTypeId ? { ...at, deactivatedDate: new Date().toISOString() } : at
      )));
    } catch (err) {
      console.error('Error deleting activity type:', err);
      setActionError(extractErrorMessage(err, 'Не вдалося архівувати тип активності. Спробуйте ще раз.'));
    }
  };

  const handleRestoreActivityType = async (activityTypeId: number) => {
    setActionError(null);
    try {
      await activityTypeService.restore(activityTypeId);
      setActivityTypes((prev) => prev.map((at) => (
        at.id === activityTypeId ? { ...at, deactivatedDate: undefined } : at
      )));
    } catch (err) {
      console.error('Error restoring activity type:', err);
      setActionError(extractErrorMessage(err, 'Не вдалося відновити тип активності. Спробуйте ще раз.'));
    }
  };

  const handleEditActivityType = (activityType: ActivityType) => {
    setSelectedActivityType(activityType);
    setEditDialogOpen(true);
  };

  const handleUpdateActivityType = async (activityTypeId: number, activityTypeData: UpdateActivityTypeRequest) => {
    try {
      const updatedActivityType = await activityTypeService.update(activityTypeId, activityTypeData);
      setActivityTypes((prev) =>
        prev.map((at) => (at.id === activityTypeId ? updatedActivityType : at))
      );
      setEditDialogOpen(false);
      setSelectedActivityType(null);
    } catch (err) {
      console.error('Error updating activity type:', err);
      throw err;
    }
  };



  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/home`)}
          size="small"
        >
          На головну
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Типи активностей
        </Typography>
      </Box>

      {/* Header */}
      {activityTypes.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 2,
            gap: 1,
          }}
        >
          <Tooltip title="Показати/сховати фільтри">
            <IconButton
              size="small"
              onClick={() => setShowFilters((prev) => !prev)}
              color={showFilters || !showOnlyActive ? 'primary' : 'default'}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Додати
          </Button>
        </Box>
      )}

      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}

      <Collapse in={showFilters}>
        <Paper
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: 'background.default',
          }}
          variant="outlined"
        >
          <FormControlLabel
            control={(
              <Checkbox
                checked={showOnlyActive}
                onChange={(e) => setShowOnlyActive(e.target.checked)}
              />
            )}
            label="Показувати лише активні"
          />
        </Paper>
      </Collapse>

      {/* Content */}
      {activityTypes.length === 0 ? (
        <Paper
          sx={{
            p: { xs: 3, sm: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Типів активностей ще немає
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Створіть перший тип активності для початку роботи
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Створити тип активності
          </Button>
        </Paper>
      ) : (
        <ActivityTypeList
          activityTypes={visibleActivityTypes}
          onDelete={handleDeleteActivityType}
          onRestore={handleRestoreActivityType}
          onEdit={handleEditActivityType}
        />
      )}

      {/* Dialogs */}
      {companyId && (
        <>
          <CreateActivityType
            open={createDialogOpen}
            companyId={companyId}
            onClose={() => setCreateDialogOpen(false)}
            onSubmit={handleCreateActivityType}
          />
          <EditActivityType
            open={editDialogOpen}
            activityType={selectedActivityType}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedActivityType(null);
            }}
            onSubmit={handleUpdateActivityType}
          />
        </>
      )}

      {confirmDialog}
    </Box>
  );
}
